import type { Key9Result } from '../types/puzzle';
import type { DayProgress } from '../storage/localSave';
import { resultLabel } from './key9Validator';

export type ShareCardVariant = 'key9_unlocked' | 'key9_missed' | 'key9_practice_complete' | 'full_done';

export type ShareMetric = {
  label: string;
  value: string;
  tone: 'accent' | 'success' | 'danger' | 'neutral';
};

export type ShareCardModel = {
  brand: 'DaySudoku';
  tagline: 'One Sudoku a Day.';
  title: string;
  body: string;
  metrics: ShareMetric[];
  key9Strip: {
    label: string;
    cells: Array<'success' | 'missed' | 'neutral'>;
  };
  fullBadge: {
    label: string;
    tone: 'success' | 'accent' | 'neutral';
  };
  footer: 'daysudoku.com';
};

export type ShareTextOptions = {
  progress?: DayProgress | null;
  fullState?: 'unlocked' | 'in_progress' | 'completed' | 'not_completed';
};

export function buildShareText(date: string, result: Key9Result, streak?: number, options: ShareTextOptions = {}): string {
  const dateLabel = formatShareDate(date);
  const status = resultLabel(result.status);
  const key9Success = result.status !== 'missed';
  const full = describeFullState(options.progress, key9Success, options.fullState);
  const streakLine = streak && streak > 1 ? `\n${streak}-day streak` : '';

  if (result.status === 'missed') {
    const lines = [
      `DaySudoku — ${dateLabel}`,
      '',
      `Key9 ❌ Missed · ${result.misses}/9 misses · ${formatTime(result.seconds)}`,
      '',
      'Key9',
      key9BlockRow(result),
      'Full',
      full.visualMark,
      '',
      'daysudoku.com'
    ];
    if (streakLine) lines.splice(lines.length - 1, 0, streakLine.trimStart());
    return lines.join('\n');
  }

  const lines = [
    `DaySudoku — ${dateLabel}`,
    '',
    `Key9 ✅ ${status} · Misses ${result.misses}/9 · ${formatTime(result.seconds)}`,
    '',
    'Key9',
    key9BlockRow(result),
    'Full',
    full.visualMark,
    '',
    'daysudoku.com'
  ];
  if (streakLine) lines.splice(lines.length - 1, 0, streakLine.trimStart());
  return lines.join('\n');
}

export function buildFullShareText(date: string, progress: DayProgress, streak?: number): string {
  const time = formatTime(progress.fullSeconds ?? 0);
  const streakLine = streak && streak > 1 ? `\n${streak}-day streak` : '';
  const fullHints = progress.fullHints ?? 0;
  const key9Line = progress.key9Result
    ? `Key9 ${progress.key9Result.status === 'missed' ? '❌' : '✅'} ${resultLabel(progress.key9Result.status)} · Misses ${progress.key9Result.misses}/9`
    : 'Key9 ✅ Unlocked';
  const key9Row = progress.key9Result
    ? key9BlockRow(progress.key9Result)
    : successBlocks();

  const lines = [
    `DaySudoku — ${formatShareDate(date)}`,
    '',
    key9Line,
    `Full ✅ Completed · Hints ${fullHints} · ${time}`,
    '',
    'Key9',
    key9Row,
    'Full',
    '✅',
    '',
    'daysudoku.com'
  ];
  if (streakLine) lines.splice(lines.length - 1, 0, streakLine.trimStart());
  return lines.join('\n');
}

export function buildPracticeShareText(date: string, practiceResult: Key9Result, officialResult?: Key9Result | null, streak?: number): string {
  const streakLine = streak && streak > 1 ? `\n${streak}-day streak` : '';
  const officialLine = officialResult?.status === 'missed'
    ? 'Official Key9 ❌ Missed'
    : officialResult
      ? `Official Key9 ✅ ${resultLabel(officialResult.status)}`
      : 'Official Key9 practice run';

  const lines = [
    `DaySudoku — ${formatShareDate(date)}`,
    '',
    `Key9 Practice ✅ Complete · Misses ${practiceResult.misses}/9 · ${formatTime(practiceResult.seconds)}`,
    officialLine,
    '',
    'Key9',
    key9BlockRow(practiceResult),
    'Full',
    'Practice',
    '',
    'daysudoku.com'
  ];
  if (streakLine) lines.splice(lines.length - 1, 0, streakLine.trimStart());
  return lines.join('\n');
}

export function buildShareCardModel(input: {
  date: string;
  variant: ShareCardVariant;
  key9Result?: Key9Result | null;
  practiceResult?: Key9Result | null;
  progress?: DayProgress | null;
  streak?: number;
}): ShareCardModel {
  const result = input.variant === 'key9_practice_complete'
    ? input.practiceResult ?? input.key9Result
    : input.progress?.key9Result ?? input.key9Result;
  const fullHints = input.progress?.fullHints ?? 0;
  const fullSeconds = input.progress?.fullSeconds ?? result?.seconds ?? 0;
  const missed = result?.status === 'missed';

  const title = getShareCardTitle(input.variant, missed);
  const body = getShareCardBody(input.variant, missed);
  const metrics: ShareMetric[] = [];

  if (input.variant === 'full_done') {
    metrics.push({
      label: 'Key9',
      value: result ? resultLabel(result.status) : 'Unlocked',
      tone: missed ? 'danger' : 'success'
    });
    metrics.push({
      label: 'Misses',
      value: `${result?.misses ?? 0}/9`,
      tone: missed ? 'danger' : 'accent'
    });
    metrics.push({
      label: 'Hints',
      value: String(fullHints),
      tone: fullHints > 0 ? 'neutral' : 'success'
    });
    metrics.push({
      label: 'Time',
      value: formatTime(fullSeconds),
      tone: 'neutral'
    });
  } else {
    metrics.push({
      label: input.variant === 'key9_practice_complete' ? 'Practice' : 'Key9',
      value: missed ? 'Missed' : 'Unlocked',
      tone: missed ? 'danger' : 'success'
    });
    metrics.push({
      label: 'Misses',
      value: `${result?.misses ?? (missed ? 9 : 0)}/9`,
      tone: missed ? 'danger' : 'accent'
    });
    metrics.push({
      label: 'Time',
      value: formatTime(result?.seconds ?? 0),
      tone: 'neutral'
    });
  }

  return {
    brand: 'DaySudoku',
    tagline: 'One Sudoku a Day.',
    title,
    body,
    metrics,
    key9Strip: {
      label: input.variant === 'key9_practice_complete' ? 'Key9 Practice 1–9' : 'Key9 1–9',
      cells: getKey9StripCells(result, input.variant)
    },
    fullBadge: getShareCardFullBadge(input.variant, input.progress, missed),
    footer: 'daysudoku.com'
  };
}

export function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function formatShareDate(date: string): string {
  const [year, month, day] = date.split('-').map((part) => Number(part));
  const parsed = Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
    ? new Date(Date.UTC(year, month - 1, day))
    : new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return `#${date}`;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function describeFullState(progress: DayProgress | null | undefined, key9Success: boolean, override?: ShareTextOptions['fullState']) {
  const fullState = override ?? (
    progress?.phase === 'full_done'
      ? 'completed'
      : progress?.phase === 'full_in_progress'
        ? 'in_progress'
        : key9Success
          ? 'unlocked'
          : 'not_completed'
  );
  const hints = progress?.fullHints ?? 0;

  switch (fullState) {
    case 'completed':
      return {
        textLine: `Full ✅ Completed · Hints ${hints}`,
        visualLine: 'Full Sudoku ✅',
        visualMark: '✅'
      };
    case 'in_progress':
      return {
        textLine: `Full ⏳ In progress · Hints ${hints}`,
        visualLine: 'Full Sudoku ⏳',
        visualMark: '⏳'
      };
    case 'unlocked':
      return {
        textLine: 'Full 🔓 Unlocked',
        visualLine: 'Full Sudoku ⬜',
        visualMark: '⬜'
      };
    case 'not_completed':
      return {
        textLine: 'Full ⬜ Not completed',
        visualLine: 'Full Sudoku ⬜',
        visualMark: '⬜'
      };
  }
}

function key9BlockRow(result: Key9Result): string {
  if (result.status !== 'missed') return successBlocks();
  const placed = normalizePlacedCount(result);
  return `${'🟩'.repeat(placed)}${'⬜'.repeat(9 - placed)}`;
}

function successBlocks(): string {
  return '🟩'.repeat(9);
}

function getKey9StripCells(result: Key9Result | null | undefined, variant: ShareCardVariant): ShareCardModel['key9Strip']['cells'] {
  if (variant === 'full_done' || variant === 'key9_unlocked' || variant === 'key9_practice_complete') {
    return Array.from({ length: 9 }, () => 'success' as const);
  }

  const placed = normalizePlacedCount(result);
  return Array.from({ length: 9 }, (_, index) => index < placed ? 'success' as const : 'neutral' as const);
}

function normalizePlacedCount(result: Key9Result | null | undefined): number {
  return Math.max(0, Math.min(9, Math.floor(Number(result?.placed) || 0)));
}

function getShareCardTitle(variant: ShareCardVariant, missed: boolean): string {
  switch (variant) {
    case 'key9_unlocked':
      return 'Key9 Unlocked';
    case 'key9_missed':
      return 'Key9 Missed';
    case 'key9_practice_complete':
      return 'Practice Complete';
    case 'full_done':
      return 'Today’s DaySudoku is done.';
  }
}

function getShareCardBody(variant: ShareCardVariant, missed: boolean): string {
  switch (variant) {
    case 'key9_unlocked':
      return '';
    case 'key9_missed':
      return '';
    case 'key9_practice_complete':
      return '';
    case 'full_done':
      return '';
  }
}

function getShareCardFullBadge(variant: ShareCardVariant, progress: DayProgress | null | undefined, missed: boolean): ShareCardModel['fullBadge'] {
  if (variant === 'full_done') {
    return { label: 'Full board complete', tone: 'success' };
  }
  if (progress?.phase === 'full_in_progress') {
    return { label: 'Full board in progress', tone: 'accent' };
  }
  if (missed) {
    return { label: 'Full not started', tone: 'neutral' };
  }
  return { label: 'Full Sudoku unlocked', tone: 'accent' };
}
